/**
 * ExecutionStatusCard - Real-time view of an in-progress batch execution.
 *
 * Used on DatasetDetailPage (Overview tab, for the most recent/active batch)
 * and elsewhere to show live status of Glue + Andes operations without the
 * UI owning any polling state. The component polls the backend every 10s
 * and updates when the user's tab is active.
 *
 * Fire-and-forget semantics: operations run on AWS; this card just observes.
 * User can close the browser any time and come back to see the final state.
 */
import React, { useCallback, useEffect, useState } from 'react';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import KeyValuePairs from '@amzn/awsui-components-console/key-value-pairs';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Alert from '@amzn/awsui-components-console/alert';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../utils/apiClient';

export interface ExecutionStatus {
  datasetId: string;
  batchId: string;
  mode: 'create' | 'append';
  snapshotDate: string;
  status: 'pending' | 'transforming' | 'andes_creating' | 'ready' | 'failed';
  statusMessage: string;
  glueJobRunId?: string;
  glueState?: string;
  glueDuration?: number;
  glueError?: string;
  andesProvider?: string;
  andesTable?: string;
  andesState?: string;
  andesVersionId?: number;
  createdAt: string;
  updatedAt: string;
}

interface ExecutionStatusCardProps {
  datasetId: string;
  batchId: string;
  /** Poll interval in ms. Default 10s. Set to 0 to disable auto-poll. */
  pollMs?: number;
  /** Called on each status update. Useful for the parent page to react to completion. */
  onUpdate?: (status: ExecutionStatus) => void;
}

const overallIndicator = (status: ExecutionStatus['status']): 'success' | 'error' | 'loading' | 'pending' | 'info' => {
  switch (status) {
    case 'ready': return 'success';
    case 'failed': return 'error';
    case 'transforming': return 'loading';
    case 'andes_creating': return 'loading';
    case 'pending': return 'pending';
    default: return 'info';
  }
};

export const ExecutionStatusCard: React.FC<ExecutionStatusCardProps> = ({ datasetId, batchId, pollMs = 10_000, onUpdate }) => {
  const [status, setStatus] = useState<ExecutionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await apiFetch(apiUrl(`/api/executions/${encodeURIComponent(datasetId)}/${encodeURIComponent(batchId)}/status`), { ...apiFetchDefaults });
      if (!r.ok) { const d = await r.json(); throw new Error(d.error || `HTTP ${r.status}`); }
      const data: ExecutionStatus = await r.json();
      setStatus(data);
      setError(null);
      if (onUpdate) onUpdate(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [datasetId, batchId, onUpdate]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Only poll while status is still in-flight. Terminal statuses (ready/failed) stop polling.
  useEffect(() => {
    if (!pollMs || !status) return;
    if (status.status === 'ready' || status.status === 'failed') return;
    const id = setInterval(fetchStatus, pollMs);
    return () => clearInterval(id);
  }, [status, pollMs, fetchStatus]);

  if (loading && !status) {
    return (
      <Container header={<Header variant="h2">Execution status</Header>}>
        <Box padding="m">Loading...</Box>
      </Container>
    );
  }

  if (error && !status) {
    return (
      <Container header={<Header variant="h2">Execution status</Header>}>
        <Alert type="error">{error}</Alert>
      </Container>
    );
  }

  if (!status) return null;

  const elapsed = status.glueDuration ? `${status.glueDuration}s` : '-';
  const isTerminal = status.status === 'ready' || status.status === 'failed';

  return (
    <Container
      header={
        <Header
          variant="h2"
          description={`Batch ${status.batchId} - ${status.mode} mode - ${status.snapshotDate}`}
          actions={<Button iconName="refresh" onClick={fetchStatus} loading={loading}>Refresh</Button>}
        >
          Execution status
        </Header>
      }
    >
      <SpaceBetween size="m">
        <StatusIndicator type={overallIndicator(status.status)}>
          <Box variant="strong" display="inline">{status.status.toUpperCase()}</Box>
          {' '}- {status.statusMessage}
        </StatusIndicator>

        <KeyValuePairs
          columns={3}
          items={[
            { label: 'Glue state', value: status.glueState || '-' },
            { label: 'Glue duration', value: elapsed },
            { label: 'Andes state', value: status.andesState || (status.mode === 'append' ? 'n/a (append mode)' : '-') },
          ]}
        />

        {status.glueError && (
          <Alert type="error" header="Glue job failed">
            {status.glueError}
          </Alert>
        )}

        {isTerminal && status.status === 'ready' && (
          <Alert type="success">
            All operations complete. You can now query the dataset in Athena
            {status.andesState === 'UNRELEASED' && ' and manually activate the Andes table in DataCentral.'}.
          </Alert>
        )}

        {!isTerminal && (
          <Box color="text-body-secondary" fontSize="body-s">
            This page auto-refreshes every {Math.round(pollMs / 1000)}s. Operations continue on AWS even if you close this tab.
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default ExecutionStatusCard;
